import { c as _c } from "react/compiler-runtime";
import { datafastIdentify } from '@/lib/datafast';
import { useAuth, useOrganization, useUser } from '@clerk/nextjs';
import { useEffect, useRef } from 'react';

/**
 * Attaches the DataFast visitor to the signed-in Clerk user.
 *
 * DataFast records pageviews against an anonymous visitor id held in the
 * `datafast_visitor_id` cookie. `identify` is what turns that anonymous trail
 * into a named profile, so the sessions a person had before signing up stay
 * joined to them afterwards.
 *
 * Deliberately driven by Clerk session state rather than by a sign-in button:
 * email/password, Google OAuth and invitation tickets all land here, and none
 * of them can fire for a visitor who is not actually authenticated.
 *
 * Fires once per Clerk session, then again when the active workspace changes,
 * so the profile carries the organization the user is currently working in.
 * Call once at the app root, inside <ClerkProvider>.
 */
export function useDataFastIdentify() {
  const $ = _c(8);
  const {
    sessionId,
    orgId
  } = useAuth();
  const {
    isLoaded,
    isSignedIn,
    user
  } = useUser();
  const {
    organization
  } = useOrganization();
  const sentRef = useRef(null);
  const userId = isLoaded && isSignedIn ? user?.id : undefined;
  const email = user?.primaryEmailAddress?.emailAddress;
  const organizationId = organization?.id ?? orgId ?? undefined;
  const organizationName = organization?.name;
  const fullName = user?.fullName ?? undefined;
  let t0;
  let t1;
  if ($[0] !== email || $[1] !== fullName || $[2] !== organizationId || $[3] !== organizationName || $[4] !== sessionId || $[5] !== userId) {
    t0 = () => {
      if (!userId || !sessionId) {
        return;
      }
      const key = `${sessionId}:${organizationId ?? ""}`;
      if (sentRef.current === key) {
        return;
      }
      sentRef.current = key;
      datafastIdentify(userId, {
        name: fullName,
        email,
        organization_id: organizationId,
        organization_name: organizationName
      });
    };
    t1 = [userId, sessionId, organizationId, organizationName, email, fullName];
    $[0] = email;
    $[1] = fullName;
    $[2] = organizationId;
    $[3] = organizationName;
    $[4] = sessionId;
    $[5] = userId;
    $[6] = t0;
    $[7] = t1;
  } else {
    t0 = $[6];
    t1 = $[7];
  }
  useEffect(t0, t1);
}